import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";

import { useAdmin } from "../../hooks/useAdmin";
import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { PanelSection } from "../common/PanelSection";

type TeamsAppType = "SingleTenant";
const outboxStatuses = [
  "",
  "queued",
  "failed_transient",
  "failed_permanent",
  "blocked",
  "expired",
  "posted",
  "skipped",
  "cancelled",
] as const;
const outboxKinds = ["", "message", "membership", "goodbye", "system"] as const;

interface TeamsOutboxRow {
  attempts: number;
  binding_id: string | null;
  content_preview: string;
  conversation_id: string | null;
  conversation_label: string | null;
  created_at: string;
  id: string;
  last_error: string | null;
  lease_expires_at: string | null;
  lease_owner: string | null;
  message_kind: string;
  posted_external_id: string | null;
  status: string;
  updated_at: string;
}

interface TeamsOutboxState {
  counts: { count: number; message_kind: string; status: string }[];
  rows: TeamsOutboxRow[];
}

interface ArchivedTeamsChannelRow {
  actor_display_name: string | null;
  actor_instance_id: string | null;
  archived_at: string;
  archived_reason: string | null;
  bound_by_user_id: string | null;
  bound_via: string | null;
  channel_display_name: string;
  channel_id: string;
  conversation_id: string | null;
  conversation_label: string | null;
  detached_at: string | null;
  detached_by_user_id: string | null;
  detached_via: string | null;
  organization_id: string;
}

interface ArchivedTeamsChannelsState {
  rows: ArchivedTeamsChannelRow[];
}

interface TeamsSetupState {
  detected_dashboard_url: string | null;
  endpoints: {
    dashboard_public_base_url: string | null;
    orchestrator_public_base_url: string | null;
  };
  registration: {
    app_type: TeamsAppType;
    bot_app_id: string;
    client_secret_set: boolean;
    environment_key: string;
    home_tenant_id: string | null;
    status: string;
    validated_at: string | null;
  } | null;
  status: {
    active_bindings_count: number | null;
    bot_status: string;
    last_webhook_activity_at: string | null;
  };
}

export const AdminTeamsTab: FC = () => {
  const {
    error: organizationsError,
    loading: organizationsLoading,
    organizations,
  } = useAdmin();
  const [state, setState] = useState<TeamsSetupState | null>(null);
  const [outbox, setOutbox] = useState<TeamsOutboxState | null>(null);
  const [archivedChannels, setArchivedChannels] =
    useState<ArchivedTeamsChannelsState | null>(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [outboxFilters, setOutboxFilters] = useState({
    conversation: "",
    kind: "",
    status: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rotatingSecret, setRotatingSecret] = useState(false);
  const [form, setForm] = useState({
    appType: "SingleTenant" as TeamsAppType,
    botAppId: "",
    clientSecret: "",
    dashboardPublicBaseUrl: "",
    environmentKey: "default",
    homeTenantId: "",
    orchestratorPublicBaseUrl: "",
  });

  const load = useCallback(async () => {
    if (!selectedOrganizationId) {
      setState(null);
      return;
    }
    setError(null);
    const response = await fetch(
      `/admin/teams/setup?${organizationQuery(selectedOrganizationId)}`,
    );
    if (!response.ok) {
      setError(await response.text());
      return;
    }
    const data = parseTeamsSetupState(await response.json());
    setState(data);
    setRotatingSecret(false);
    setForm((current) => ({
      ...current,
      appType: "SingleTenant",
      botAppId: data.registration?.bot_app_id ?? current.botAppId,
      dashboardPublicBaseUrl:
        data.endpoints.dashboard_public_base_url ??
        data.detected_dashboard_url ??
        current.dashboardPublicBaseUrl,
      environmentKey:
        data.registration?.environment_key ?? current.environmentKey,
      homeTenantId: data.registration?.home_tenant_id ?? current.homeTenantId,
      orchestratorPublicBaseUrl:
        data.endpoints.orchestrator_public_base_url ??
        current.orchestratorPublicBaseUrl,
    }));
  }, [selectedOrganizationId]);

  const loadOutbox = useCallback(async () => {
    if (!selectedOrganizationId) {
      setOutbox(null);
      return;
    }
    const params = new URLSearchParams();
    params.set("organization_id", selectedOrganizationId);
    if (outboxFilters.status) {
      params.set("status", outboxFilters.status);
    }
    if (outboxFilters.kind) {
      params.set("kind", outboxFilters.kind);
    }
    if (outboxFilters.conversation.trim()) {
      params.set("conversation", outboxFilters.conversation.trim());
    }
    const response = await fetch(`/admin/teams/outbox?${params.toString()}`);
    if (!response.ok) {
      setError(await response.text());
      return;
    }
    setOutbox(parseTeamsOutboxState(await response.json()));
  }, [outboxFilters, selectedOrganizationId]);

  const loadArchivedChannels = useCallback(async () => {
    if (!selectedOrganizationId) {
      setArchivedChannels(null);
      return;
    }
    const params = new URLSearchParams({
      limit: "100",
      organization_id: selectedOrganizationId,
    });
    const response = await fetch(
      `/admin/teams/archived-channels?${params.toString()}`,
    );
    if (!response.ok) {
      setError(await response.text());
      return;
    }
    setArchivedChannels(parseArchivedTeamsChannelsState(await response.json()));
  }, [selectedOrganizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadOutbox();
  }, [loadOutbox]);

  useEffect(() => {
    void loadArchivedChannels();
  }, [loadArchivedChannels]);

  const submit = async () => {
    if (!selectedOrganizationId) {
      setError("Select an organization before saving Teams setup.");
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const response = await fetch("/admin/teams/setup", {
        body: JSON.stringify({
          appType: "SingleTenant",
          botAppId: form.botAppId,
          clientSecret: form.clientSecret,
          dashboardPublicBaseUrl: form.dashboardPublicBaseUrl,
          environmentKey: form.environmentKey,
          homeTenantId: form.homeTenantId,
          orchestratorPublicBaseUrl: form.orchestratorPublicBaseUrl,
          organization_id: selectedOrganizationId,
        }),
        headers: { "content-type": "application/json" },
        method: "POST",
      });
      if (!response.ok) {
        setError(await response.text());
        return;
      }
      setSaved(true);
      setForm((current) => ({ ...current, clientSecret: "" }));
      await load();
    } finally {
      setSaving(false);
    }
  };

  const manifestHref = selectedOrganizationId
    ? `/admin/teams/manifest.zip?${organizationQuery(selectedOrganizationId)}`
    : null;
  const secretSet = state?.registration?.client_secret_set ?? false;
  const showSecretInput = !secretSet || rotatingSecret;
  const submitLabel = getSubmitLabel({ secretSet, showSecretInput });

  return (
    <PanelSection title="Microsoft Teams">
      <div className="space-y-4">
        <p className="font-mono text-[11px] text-slate-600 dark:text-slate-300">
          Configure the ARTI Teams bot registration and callback URLs.
        </p>
        <AdminTeamsOrganizationSelector
          loading={organizationsLoading}
          organizations={organizations}
          selectedOrganizationId={selectedOrganizationId}
          onSelect={(organizationId) => {
            setSelectedOrganizationId(organizationId);
            setError(null);
            setSaved(false);
          }}
        />
        {organizationsError && (
          <Alert variant="error">{organizationsError}</Alert>
        )}
        {!selectedOrganizationId && (
          <Alert variant="info">
            Select an organization before viewing or changing Teams admin state.
          </Alert>
        )}
        {error && <Alert variant="error">{error}</Alert>}
        {saved && <Alert variant="success">Teams setup saved.</Alert>}

        {selectedOrganizationId && (
          <>
            <TeamsSetupGroup
              helper="Identity — the Azure bot registration this orchestrator speaks for."
              title="[ IDENTITY ]"
            >
              <Field
                label="Bot App ID"
                value={form.botAppId}
                onChange={(botAppId) => setForm({ ...form, botAppId })}
              />
              <ReadOnlyField label="App Type" value="SingleTenant" />
              <Field
                label="Home Tenant ID"
                value={form.homeTenantId}
                onChange={(homeTenantId) => setForm({ ...form, homeTenantId })}
              />
            </TeamsSetupGroup>

            <TeamsSetupGroup
              helper="Deployment — public URLs that Bot Framework calls back into."
              title="[ DEPLOYMENT ]"
            >
              <Field
                label="Public Orchestrator URL"
                value={form.orchestratorPublicBaseUrl}
                onChange={(orchestratorPublicBaseUrl) =>
                  setForm({ ...form, orchestratorPublicBaseUrl })
                }
              />
              <Field
                label="Dashboard URL"
                value={form.dashboardPublicBaseUrl}
                onChange={(dashboardPublicBaseUrl) =>
                  setForm({ ...form, dashboardPublicBaseUrl })
                }
              />
              <Field
                label="Environment Key"
                value={form.environmentKey}
                onChange={(environmentKey) =>
                  setForm({ ...form, environmentKey })
                }
              />
            </TeamsSetupGroup>

            <TeamsSetupGroup
              helper="Auth — client secret used to mint Microsoft tokens."
              title="[ AUTH ]"
            >
              <TeamsClientSecretField
                onChange={(clientSecret) => setForm({ ...form, clientSecret })}
                onRotate={() => setRotatingSecret(true)}
                rotating={rotatingSecret}
                secretSet={secretSet}
                value={form.clientSecret}
              />
            </TeamsSetupGroup>

            <div className="flex flex-wrap gap-2">
              <Button
                loading={saving}
                onClick={() => void submit()}
                type="button"
              >
                {submitLabel}
              </Button>
              {manifestHref ? (
                <a href={manifestHref} className="inline-flex">
                  <Button type="button" variant="secondary">
                    [ GENERATE MANIFEST ]
                  </Button>
                </a>
              ) : (
                <Button type="button" variant="secondary">
                  [ GENERATE MANIFEST ]
                </Button>
              )}
            </div>

            <TeamsStatusBlock state={state} />
            <ArchivedTeamsChannelsPanel archivedChannels={archivedChannels} />
            <TeamsOutboxPanel
              filters={outboxFilters}
              onAction={async (rowId, action) => {
                if (!selectedOrganizationId) {
                  setError(
                    "Select an organization before changing outbox rows.",
                  );
                  return;
                }
                const response = await fetch(
                  `/admin/teams/outbox/${rowId}/${action}`,
                  {
                    body: JSON.stringify({
                      organization_id: selectedOrganizationId,
                    }),
                    headers: { "content-type": "application/json" },
                    method: "POST",
                  },
                );
                if (!response.ok) {
                  setError(await response.text());
                  return;
                }
                await loadOutbox();
              }}
              onFiltersChange={setOutboxFilters}
              outbox={outbox}
            />
          </>
        )}
      </div>
    </PanelSection>
  );
};

const AdminTeamsOrganizationSelector: FC<{
  loading: boolean;
  onSelect: (organizationId: string) => void;
  organizations: readonly {
    name: string;
    organization_id: string;
    slug: string;
  }[];
  selectedOrganizationId: string;
}> = ({ loading, onSelect, organizations, selectedOrganizationId }) => (
  <section className="border border-slate-700/70 bg-slate-950/40 p-3">
    <label className="space-y-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
      <span>Organization</span>
      <select
        className="w-full border border-slate-700 bg-slate-900 px-3 py-2 text-sm normal-case tracking-normal text-slate-100 outline-none focus:border-cyan-400/70"
        disabled={loading}
        onChange={(event) => onSelect(event.target.value)}
        value={selectedOrganizationId}
      >
        <option value="">
          {loading ? "Loading organizations..." : "Select an organization"}
        </option>
        {organizations.map((organization) => (
          <option
            key={organization.organization_id}
            value={organization.organization_id}
          >
            {organization.name} ({organization.slug})
          </option>
        ))}
      </select>
    </label>
  </section>
);

function organizationQuery(organizationId: string): string {
  return new URLSearchParams({ organization_id: organizationId }).toString();
}

const TeamsSetupGroup: FC<{
  children: ReactNode;
  helper: string;
  title: string;
}> = ({ children, helper, title }) => (
  <section className="border border-slate-700/70 bg-slate-950/40 p-3">
    <div className="mb-3">
      <p className="font-mono text-[10px] font-bold uppercase tracking-[0.24em] text-cyan-300/80">
        {title}
      </p>
      <p className="mt-1 font-mono text-[10px] text-slate-400">{helper}</p>
    </div>
    <div className="grid gap-3 md:grid-cols-3">{children}</div>
  </section>
);

function Field(props: {
  label: string;
  onChange: (value: string) => void;
  type?: string;
  value: string;
}) {
  return (
    <label className="space-y-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
      <span>{props.label}</span>
      <input
        className="w-full border border-slate-700 bg-slate-900 px-3 py-2 text-sm normal-case tracking-normal text-slate-100 outline-none focus:border-cyan-400/70"
        onChange={(event) => props.onChange(event.target.value)}
        type={props.type ?? "text"}
        value={props.value}
      />
    </label>
  );
}

function ReadOnlyField(props: { label: string; value: string }) {
  return (
    <div className="space-y-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
      <span>{props.label}</span>
      <div className="w-full border border-slate-700 bg-slate-900 px-3 py-2 text-sm normal-case tracking-normal text-slate-100 outline-none focus:border-cyan-400/70">
        {props.value}
      </div>
    </div>
  );
}

function getSubmitLabel(input: {
  secretSet: boolean;
  showSecretInput: boolean;
}): string {
  if (!input.showSecretInput) {
    return "[ SAVE TEAMS SETUP ]";
  }
  if (input.secretSet) {
    return "[ SAVE ROTATION ]";
  }
  return "[ SET CLIENT SECRET ]";
}

export const TeamsClientSecretField: FC<{
  onChange: (value: string) => void;
  onRotate: () => void;
  rotating: boolean;
  secretSet: boolean;
  value: string;
}> = ({ onChange, onRotate, rotating, secretSet, value }) => {
  if (secretSet && !rotating) {
    return (
      <div className="space-y-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        <span>Client Secret</span>
        <div className="flex border border-emerald-400/30 bg-slate-900">
          <span className="flex-1 px-3 py-2 text-sm tracking-[0.2em] text-slate-300">
            •••••• <span className="text-emerald-300">[SET]</span>
          </span>
          <button
            className="border-l border-emerald-400/30 px-3 text-[10px] text-cyan-300 transition-colors hover:bg-cyan-400/10"
            onClick={onRotate}
            type="button"
          >
            [ ROTATE ]
          </button>
        </div>
      </div>
    );
  }
  return (
    <Field
      label="Client Secret"
      type="password"
      value={value}
      onChange={onChange}
    />
  );
};

const TeamsStatusBlock: FC<{ state: TeamsSetupState | null }> = ({ state }) => (
  <section className="border border-slate-700/70 bg-slate-950/40 p-3">
    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.24em] text-emerald-300/80">
      [ STATUS ]
    </p>
    <div className="mt-3 grid gap-2 font-mono text-[11px] text-slate-300 md:grid-cols-3">
      <StatusValue label="Bot" value={state?.status.bot_status ?? "—"} />
      <StatusValue
        label="Last webhook"
        value={state?.status.last_webhook_activity_at ?? "—"}
      />
      <StatusValue
        label="Active bindings"
        value={
          state?.status.active_bindings_count == null
            ? "—"
            : String(state.status.active_bindings_count)
        }
      />
    </div>
  </section>
);

const StatusValue: FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="border border-slate-800 bg-slate-900/60 px-2 py-1.5">
    <p className="text-[9px] uppercase tracking-[0.18em] text-slate-500">
      {label}
    </p>
    <p className="mt-1 text-slate-100">{value}</p>
  </div>
);

export const ArchivedTeamsChannelsPanel: FC<{
  archivedChannels: ArchivedTeamsChannelsState | null;
}> = ({ archivedChannels }) => (
  <section className="border border-slate-700/70 bg-slate-950/40 p-3">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.24em] text-red-300/80">
          [ ARCHIVED CHANNELS ]
        </p>
        <p className="mt-1 font-mono text-[10px] text-slate-400">
          Teams-created channels archived after their binding detached.
        </p>
      </div>
      <span className="border border-slate-700 bg-slate-900 px-2 py-1 font-mono text-[10px] text-slate-300">
        {archivedChannels?.rows.length ?? 0} rows
      </span>
    </div>
    <div className="mt-3 space-y-2">
      {(archivedChannels?.rows ?? []).map((row) => (
        <div
          key={row.channel_id}
          className="border border-slate-800 bg-slate-900/60 p-2 font-mono text-[10px] text-slate-300"
        >
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[11px] text-slate-100">
                {row.channel_display_name} ·{" "}
                {row.actor_display_name ?? "actor unknown"}
              </p>
              <p
                className="truncate text-slate-400"
                title={row.conversation_id ?? ""}
              >
                {row.conversation_label ?? "Teams conversation"} ·{" "}
                {row.conversation_id ?? "no conversation"}
              </p>
              <p className="text-slate-500">
                archived {row.archived_at} ·{" "}
                {row.archived_reason ?? "no reason"}
              </p>
              <p className="text-slate-500">
                bound via {row.bound_via ?? "—"} by{" "}
                {row.bound_by_user_id ?? "—"} · detached via{" "}
                {row.detached_via ?? "—"} by {row.detached_by_user_id ?? "—"} ·{" "}
                {row.detached_at ?? "not detached"}
              </p>
            </div>
          </div>
        </div>
      ))}
      {archivedChannels && archivedChannels.rows.length === 0 && (
        <p className="font-mono text-[11px] text-slate-500">
          No archived Teams-created channels.
        </p>
      )}
    </div>
  </section>
);

export const TeamsOutboxPanel: FC<{
  filters: { conversation: string; kind: string; status: string };
  onAction: (
    rowId: string,
    action: "cancel" | "retry" | "skip",
  ) => Promise<void>;
  onFiltersChange: (filters: {
    conversation: string;
    kind: string;
    status: string;
  }) => void;
  outbox: TeamsOutboxState | null;
}> = ({ filters, onAction, onFiltersChange, outbox }) => (
  <section className="border border-slate-700/70 bg-slate-950/40 p-3">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.24em] text-cyan-300/80">
          [ OUTBOX ]
        </p>
        <p className="mt-1 font-mono text-[10px] text-slate-400">
          Teams delivery rows, including detached conversation snapshots.
        </p>
      </div>
      <div className="flex flex-wrap gap-2 font-mono text-[10px]">
        {(outbox?.counts ?? []).map((count) => (
          <span
            key={`${count.status}:${count.message_kind}`}
            className="border border-slate-700 bg-slate-900 px-2 py-1 text-slate-300"
          >
            {count.status}/{count.message_kind}: {count.count}
          </span>
        ))}
      </div>
    </div>
    <div className="mt-3 grid gap-2 md:grid-cols-3">
      <select
        className="border border-slate-700 bg-slate-900 px-2 py-1.5 font-mono text-[11px] text-slate-100"
        value={filters.status}
        onChange={(event) =>
          onFiltersChange({ ...filters, status: event.target.value })
        }
      >
        {outboxStatuses.map((status) => (
          <option key={status || "all"} value={status}>
            {status || "all statuses"}
          </option>
        ))}
      </select>
      <select
        className="border border-slate-700 bg-slate-900 px-2 py-1.5 font-mono text-[11px] text-slate-100"
        value={filters.kind}
        onChange={(event) =>
          onFiltersChange({ ...filters, kind: event.target.value })
        }
      >
        {outboxKinds.map((kind) => (
          <option key={kind || "all"} value={kind}>
            {kind || "all kinds"}
          </option>
        ))}
      </select>
      <input
        className="border border-slate-700 bg-slate-900 px-2 py-1.5 font-mono text-[11px] text-slate-100"
        placeholder="conversation filter"
        value={filters.conversation}
        onChange={(event) =>
          onFiltersChange({ ...filters, conversation: event.target.value })
        }
      />
    </div>
    <div className="mt-3 space-y-2">
      {(outbox?.rows ?? []).map((row) => (
        <div
          key={row.id}
          className="border border-slate-800 bg-slate-900/60 p-2 font-mono text-[10px] text-slate-300"
        >
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[11px] text-slate-100">
                {row.status} · {row.message_kind} · attempts {row.attempts}
              </p>
              <p
                className="truncate text-slate-400"
                title={row.conversation_id ?? ""}
              >
                {row.conversation_label ?? "Teams conversation"} ·{" "}
                {row.conversation_id ?? "no conversation"}
              </p>
              <p className="truncate text-slate-500">{row.content_preview}</p>
              {row.last_error && (
                <p className="mt-1 whitespace-pre-wrap text-red-300">
                  {row.last_error}
                </p>
              )}
              <p className="mt-1 text-slate-500">
                queued {row.created_at} · updated {row.updated_at} · lease{" "}
                {row.lease_owner ?? "—"} / {row.lease_expires_at ?? "—"}
              </p>
            </div>
            <div className="flex shrink-0 gap-1">
              <OutboxActionButton
                disabled={!canRetryOutboxRow(row)}
                label="[ RETRY ]"
                onClick={() => onAction(row.id, "retry")}
              />
              <OutboxActionButton
                label="[ SKIP ]"
                onClick={() => onAction(row.id, "skip")}
              />
              <OutboxActionButton
                label="[ CANCEL ]"
                onClick={() => onAction(row.id, "cancel")}
              />
            </div>
          </div>
        </div>
      ))}
      {outbox && outbox.rows.length === 0 && (
        <p className="font-mono text-[11px] text-slate-500">
          No outbox rows match the current filters.
        </p>
      )}
    </div>
  </section>
);

const OutboxActionButton: FC<{
  disabled?: boolean;
  label: string;
  onClick: () => Promise<void>;
}> = ({ disabled = false, label, onClick }) => (
  <button
    className="border border-cyan-400/30 px-2 py-1 text-cyan-300 transition-colors hover:bg-cyan-400/10 disabled:border-slate-700 disabled:text-slate-600"
    disabled={disabled}
    onClick={() => void onClick()}
    type="button"
  >
    {label}
  </button>
);

function canRetryOutboxRow(row: TeamsOutboxRow): boolean {
  return row.status === "failed_transient" || row.status === "blocked";
}

function parseTeamsSetupState(value: unknown): TeamsSetupState {
  if (typeof value !== "object" || value === null) {
    throw new Error("Invalid Teams setup response.");
  }
  const candidate = value;
  if (
    !("detected_dashboard_url" in candidate) ||
    !("endpoints" in candidate) ||
    !("registration" in candidate) ||
    !("status" in candidate)
  ) {
    throw new Error("Invalid Teams setup response.");
  }
  return {
    detected_dashboard_url:
      typeof candidate.detected_dashboard_url === "string"
        ? candidate.detected_dashboard_url
        : null,
    endpoints: parseTeamsSetupEndpoints(candidate.endpoints),
    registration: parseTeamsSetupRegistration(candidate.registration),
    status: parseTeamsSetupStatus(candidate.status),
  };
}

function parseTeamsSetupEndpoints(
  value: unknown,
): TeamsSetupState["endpoints"] {
  if (typeof value !== "object" || value === null) {
    throw new Error("Invalid Teams setup response.");
  }
  return {
    dashboard_public_base_url:
      "dashboard_public_base_url" in value &&
      typeof value.dashboard_public_base_url === "string"
        ? value.dashboard_public_base_url
        : null,
    orchestrator_public_base_url:
      "orchestrator_public_base_url" in value &&
      typeof value.orchestrator_public_base_url === "string"
        ? value.orchestrator_public_base_url
        : null,
  };
}

function parseTeamsSetupRegistration(
  value: unknown,
): TeamsSetupState["registration"] {
  if (value === null) {
    return null;
  }
  if (typeof value !== "object" || value === null) {
    throw new Error("Invalid Teams setup response.");
  }
  if (
    !("app_type" in value) ||
    !("bot_app_id" in value) ||
    !("client_secret_set" in value) ||
    !("environment_key" in value) ||
    !("status" in value)
  ) {
    throw new Error("Invalid Teams setup response.");
  }
  const appType = parseTeamsAppType(value.app_type);
  if (
    !appType ||
    typeof value.bot_app_id !== "string" ||
    typeof value.client_secret_set !== "boolean" ||
    typeof value.environment_key !== "string" ||
    typeof value.status !== "string"
  ) {
    throw new Error("Invalid Teams setup response.");
  }
  return {
    app_type: appType,
    bot_app_id: value.bot_app_id,
    client_secret_set: value.client_secret_set,
    environment_key: value.environment_key,
    home_tenant_id:
      "home_tenant_id" in value && typeof value.home_tenant_id === "string"
        ? value.home_tenant_id
        : null,
    status: value.status,
    validated_at:
      "validated_at" in value && typeof value.validated_at === "string"
        ? value.validated_at
        : null,
  };
}

function parseTeamsSetupStatus(value: unknown): TeamsSetupState["status"] {
  if (typeof value !== "object" || value === null) {
    return {
      active_bindings_count: null,
      bot_status: "idle",
      last_webhook_activity_at: null,
    };
  }
  return {
    active_bindings_count:
      "active_bindings_count" in value &&
      typeof value.active_bindings_count === "number"
        ? value.active_bindings_count
        : null,
    bot_status:
      "bot_status" in value && typeof value.bot_status === "string"
        ? value.bot_status
        : "idle",
    last_webhook_activity_at:
      "last_webhook_activity_at" in value &&
      typeof value.last_webhook_activity_at === "string"
        ? value.last_webhook_activity_at
        : null,
  };
}

function parseArchivedTeamsChannelsState(
  value: unknown,
): ArchivedTeamsChannelsState {
  if (typeof value !== "object" || value === null) {
    throw new Error("Invalid archived Teams channels response.");
  }
  const rows = Object.getOwnPropertyDescriptor(value, "rows")?.value;
  return {
    rows: Array.isArray(rows) ? rows.map(parseArchivedTeamsChannelRow) : [],
  };
}

function parseArchivedTeamsChannelRow(value: unknown): ArchivedTeamsChannelRow {
  if (typeof value !== "object" || value === null) {
    throw new Error("Invalid archived Teams channels response.");
  }
  return {
    actor_display_name: readNullableString(value, "actor_display_name"),
    actor_instance_id: readNullableString(value, "actor_instance_id"),
    archived_at: readString(value, "archived_at"),
    archived_reason: readNullableString(value, "archived_reason"),
    bound_by_user_id: readNullableString(value, "bound_by_user_id"),
    bound_via: readNullableString(value, "bound_via"),
    channel_display_name: readString(value, "channel_display_name"),
    channel_id: readString(value, "channel_id"),
    conversation_id: readNullableString(value, "conversation_id"),
    conversation_label: readNullableString(value, "conversation_label"),
    detached_at: readNullableString(value, "detached_at"),
    detached_by_user_id: readNullableString(value, "detached_by_user_id"),
    detached_via: readNullableString(value, "detached_via"),
    organization_id: readString(value, "organization_id"),
  };
}

function parseTeamsOutboxState(value: unknown): TeamsOutboxState {
  if (typeof value !== "object" || value === null) {
    throw new Error("Invalid Teams outbox response.");
  }
  if (!("counts" in value) || !("rows" in value)) {
    throw new Error("Invalid Teams outbox response.");
  }
  return {
    counts: Array.isArray(value.counts)
      ? value.counts.map(parseTeamsOutboxCount)
      : [],
    rows: Array.isArray(value.rows) ? value.rows.map(parseTeamsOutboxRow) : [],
  };
}

function readString(value: object, key: string): string {
  const property = Object.getOwnPropertyDescriptor(value, key)?.value;
  return typeof property === "string" ? property : "";
}

function readNullableString(value: object, key: string): string | null {
  const property = Object.getOwnPropertyDescriptor(value, key)?.value;
  return typeof property === "string" ? property : null;
}

function parseTeamsOutboxCount(
  value: unknown,
): TeamsOutboxState["counts"][number] {
  if (typeof value !== "object" || value === null) {
    throw new Error("Invalid Teams outbox response.");
  }
  return {
    count:
      "count" in value && typeof value.count === "number" ? value.count : 0,
    message_kind:
      "message_kind" in value && typeof value.message_kind === "string"
        ? value.message_kind
        : "message",
    status:
      "status" in value && typeof value.status === "string"
        ? value.status
        : "blocked",
  };
}

function parseTeamsOutboxRow(value: unknown): TeamsOutboxRow {
  if (typeof value !== "object" || value === null) {
    throw new Error("Invalid Teams outbox response.");
  }
  return {
    attempts:
      "attempts" in value && typeof value.attempts === "number"
        ? value.attempts
        : 0,
    binding_id:
      "binding_id" in value && typeof value.binding_id === "string"
        ? value.binding_id
        : null,
    content_preview:
      "content_preview" in value && typeof value.content_preview === "string"
        ? value.content_preview
        : "",
    conversation_id:
      "conversation_id" in value && typeof value.conversation_id === "string"
        ? value.conversation_id
        : null,
    conversation_label:
      "conversation_label" in value &&
      typeof value.conversation_label === "string"
        ? value.conversation_label
        : null,
    created_at:
      "created_at" in value && typeof value.created_at === "string"
        ? value.created_at
        : "",
    id: "id" in value && typeof value.id === "string" ? value.id : "",
    last_error:
      "last_error" in value && typeof value.last_error === "string"
        ? value.last_error
        : null,
    lease_expires_at:
      "lease_expires_at" in value && typeof value.lease_expires_at === "string"
        ? value.lease_expires_at
        : null,
    lease_owner:
      "lease_owner" in value && typeof value.lease_owner === "string"
        ? value.lease_owner
        : null,
    message_kind:
      "message_kind" in value && typeof value.message_kind === "string"
        ? value.message_kind
        : "message",
    posted_external_id:
      "posted_external_id" in value &&
      typeof value.posted_external_id === "string"
        ? value.posted_external_id
        : null,
    status:
      "status" in value && typeof value.status === "string"
        ? value.status
        : "blocked",
    updated_at:
      "updated_at" in value && typeof value.updated_at === "string"
        ? value.updated_at
        : "",
  };
}

function parseTeamsAppType(value: unknown): TeamsAppType | null {
  if (value === "SingleTenant") {
    return "SingleTenant";
  }
  return null;
}
